import { Component, Input, OnInit, Output, EventEmitter } from '@angular/core';
import {
  AbstractControl,
  FormArray,
  FormBuilder,
  FormGroup
} from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import { BaseSelectable } from '../selectable.base';

export interface SelectableItem {
  id: number;
  name: string;
  selected: boolean;
}

export interface SelectedItemResult {
  items: SelectableItem[];
}

function ValidateSelectableItems(
  control: AbstractControl
): { noSelection: boolean } {
  const values: { selected: boolean }[] = (control as FormGroup).get(
    'lineItems'
  ).value;
  return values.find(p => p.selected) === undefined || false
    ? { noSelection: true }
    : null;
}

@Component({
  selector: 'iq-selectable-list',
  templateUrl: './selectable-list.component.html',
  styleUrls: ['./selectable-list.component.scss']
})
export class SelectableListComponent extends BaseSelectable<SelectedItemResult>
  implements OnInit {
  private _itemsformArray: AbstractControl[];

  @Input()
  public get itemsFormArray() {
    return this._itemsformArray;
  }

  public set itemsFormArray(val: AbstractControl[]) {
    this._itemsformArray = val;
  }

  private _selectableItems: SelectableItem[];
  @Input()
  public get selectableItems() {
    return this._selectableItems;
  }

  public set selectableItems(val: SelectableItem[]) {
    this._selectableItems = val;
    this.updateSelection();
  }

  @Input() allowEmptySelection = false;

  @Output() checked = new EventEmitter<SelectableItem>();

  constructor(
    private formBuilder: FormBuilder,
    private translateService: TranslateService
  ) {
    super();
  }

  private initLineItems(lineItems: SelectableItem[]): FormArray {
    return this.formBuilder.array(
      lineItems.map(selectableItem => this.initLineItem(selectableItem))
    );
  }

  private initLineItem(selectableItem: SelectableItem): FormGroup {
    const formGroup = this.formBuilder.group({
      id: this.formBuilder.control({
        value: selectableItem.id,
        disabled: true
      }),
      name: this.formBuilder.control({
        value: selectableItem.name,
        disabled: true
      }),
      selected: this.formBuilder.control(selectableItem.selected)
    });
    return formGroup;
  }

  submit(event: Event) {
    if (this.formGroup.validator === null && !this.allowEmptySelection) {
      this.formGroup.setValidators(ValidateSelectableItems);
    }
    this.formGroup.updateValueAndValidity({ emitEvent: false, onlySelf: true });
    if (this.formGroup.valid) {
      this.errorMessage = null;
      const items: SelectableItem[] = this.itemsFormArray.map(
        (p: FormGroup) => ({
          id: p.get('id').value,
          selected: p.get('selected').value,
          name: p.get('name').value
        })
      );
      this.validSelection.emit({ result: { items: items } });
    } else {
      this.errorMessage = this.translateService.instant(this.errorCode);
    }
  }

  ngOnInit() {
    this.updateSelection();
  }

  onChecked(e, item) {
    this.checked.emit({
      id: item.get('id').value,
      selected: e.target.checked,
      name: item.get('name').value
    });
  }

  private updateSelection() {
    this.formGroup = this.formBuilder.group(
      { lineItems: this.initLineItems(this.selectableItems) },
      { updateOn: 'submit' }
    );
    this.itemsFormArray = (this.formGroup.get(
      'lineItems'
    ) as FormArray).controls;
  }
}
